// src/components/hero/SpecialOfferBanner.jsx
import { X, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-scroll";
import Countdown from "../hero/Countdown";

// Helper function to calculate discount percentage
const calculateDiscountPercentage = (regularPrice, salePrice) => {
  if (!salePrice || !regularPrice) return 0;
  const regular = parseFloat(String(regularPrice).replace(/[^0-9.]/g, ""));
  const sale = parseFloat(String(salePrice).replace(/[^0-9.]/g, ""));
  if (regular === 0 || isNaN(regular) || isNaN(sale)) return 0;
  return Math.round(((regular - sale) / regular) * 100);
};

export const SpecialOfferBanner = ({ prices, onClose, isVisible }) => {
  const [hasSpecialOffer, setHasSpecialOffer] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [endDate, setEndDate] = useState(null);

  // Check for special offers over 49% on price data change
  useEffect(() => {
    if (prices && prices.length > 0) {
      let maxDiscount = 0;
      let foundSpecialOffer = false;
      let offerEndDate = null;

      prices.forEach((price) => {
        if (price.on_sale && price.sale_price && price.price) {
          const discount = calculateDiscountPercentage(
            price.price,
            price.sale_price
          );

          if (discount > 0) {
            foundSpecialOffer = true;
            if (discount > maxDiscount) {
              maxDiscount = discount;
              // Only use the sale end date from the store - no fallback
              if (price.date_on_sale_to_gmt) {
                offerEndDate = price.date_on_sale_to_gmt;
              }
            }
          }
        }
      });

      if (foundSpecialOffer) {
        setDiscountPercentage(maxDiscount);
        setHasSpecialOffer(true);
        setEndDate(offerEndDate);
      }
    }
  }, [prices]);

  // Don't render if no special offer or parent says not visible
  if (!hasSpecialOffer || !isVisible) return null;

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="special-offer-banner fixed mr-0.5 top-0.5 left-0 right-0 w-full bg-gradient-to-r from-neutral-900 via-neutral-800 to-neutral-900 z-[110] border-b border-neutral-700/50">
      {/* Subtle animated accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-fuchsia-500 to-transparent opacity-80"></div>

      <div className="relative flex items-center justify-between w-full px-2 sm:px-4 py-2.5 sm:py-3">
        {/* Main content - centered on desktop, full width on mobile */}
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-center flex-1 mx-auto max-w-full">
          {/* Mobile Layout */}
          <div className="flex sm:hidden flex-col items-center gap-1.5 w-full text-center">
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <span className="text-sm text-neutral-300">
                Limited Time Offer
              </span>
              <span className="font-semibold text-base text-white bg-gradient-to-r from-fuchsia-600 to-pink-600 bg-clip-text text-transparent">
                Save {discountPercentage}%
              </span>
            </div>
            <Link
              activeClass="active"
              spy={true}
              to="pricing"
              smooth="easeInOutQuint"
              duration={1000}
              delay={200}
              offset={20}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-white text-neutral-900 rounded-md hover:bg-neutral-100 transition-all duration-200 cursor-pointer text-sm font-medium"
              onClick={handleClose}
            >
              <span>View Pricing</span>
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:flex items-center gap-3 lg:gap-4">
            {/* Discount badge with countdown */}
            <div className="flex items-center gap-2 px-3 py-1 bg-neutral-800/50 rounded-full border border-neutral-700/50">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-neutral-400 font-medium">
                LIMITED TIME
              </span>
              {endDate && (
                <>
                  <span className="text-neutral-600">|</span>
                  <div className="text-xs font-mono text-neutral-300">
                    <Countdown targetDate={endDate} shortVersion={true} />
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-3">
              <span className="text-neutral-300 text-sm lg:text-base">
                Special Offer:
              </span>
              <span className="font-bold text-lg lg:text-xl text-white">
                Save up to {discountPercentage}%
              </span>
            </div>

            <Link
              activeClass="active"
              spy={true}
              to="pricing"
              smooth="easeInOutQuint"
              duration={1000}
              delay={200}
              offset={20}
              className="inline-flex items-center gap-1.5 ml-2 px-4 py-1.5 bg-white text-neutral-900 rounded-md hover:bg-neutral-100 transition-all duration-200 cursor-pointer font-medium text-sm lg:text-base"
              onClick={handleClose}
            >
              <span>View Pricing</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* Close button - more subtle */}
        <button
          onClick={handleClose}
          className="absolute right-2 sm:right-3 lg:right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300 transition-colors duration-200 p-1.5 hover:bg-neutral-800/50 rounded-md"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
